import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Button, ClickAwayListener } from '@mui/material';
import { Plus, UserPlus } from 'lucide-react';

import { AddIcon } from '@labradorsports/assets';
import { SimpleModal, StyledSnackbar, LoadingSpinner } from '@labradorsports/components';
import { useSite } from '@labradorsports/utils';

import { useDispatcher } from '../../shared/hooks/index.js';
import { useNavigateWithQuery } from '../../shared/providers/index.js';
import {
    routerActions,
    Selectors,
    useFetchTeamsQuery,
    useGetProfileSettingsQuery,
    useLeaveTeamMutation,
    useUpdateProfileSettingsMutation,
} from '../../store/index.js';
import { JoinTeam, JoinTeamSuccess } from '../index.js';
import TeamsMenuProgram from '../teams-menu-program/teams-menu-program.js';

interface Props {
    onSelect?: (team: any) => any;
    onOpen?: () => any;
}

const TeamsMenu: FC<Props> = ({ onOpen, onSelect }) => {
    const { Config } = useSite();
    const setActiveTeam = useDispatcher(routerActions.SetActiveTeam);
    const currentTeam = useSelector(Selectors.currentTeam);
    const uid = useSelector(Selectors.uid);
    const { data: profileSettings = {}, isLoading: isProfileSettingsLoading } = useGetProfileSettingsQuery({ uid });
    const {
        data: { ownedTeams = [], joinedTeams = [], pendingJoinTeams = [], programs = [] } = {},
        isUninitialized,
        isLoading,
        isSuccess,
    } = useFetchTeamsQuery();
    const [leaveTeam] = useLeaveTeamMutation();
    const [updateProfileSettings] = useUpdateProfileSettingsMutation();
    const location = useLocation();
    const navigate = useNavigateWithQuery();
    const [showDropdown, setShowDropdown] = useState(false);
    const [showJoin, setShowJoin] = useState(false);
    const [joinResult, setJoinResult] = useState(null);
    const [showUnpaidMessage, setShowUnpaidMessage] = useState(false);
    const [showUnpaidMore, setShowUnpaidMore] = useState(false);
    const [leavingTeam, setLeavingTeam] = useState(null);

    const teams = ownedTeams.concat(joinedTeams).concat(pendingJoinTeams);
    const currentProgram = programs.find((program) => program.id === currentTeam?.programId);

    useEffect(() => {
        setShowDropdown(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isProfileSettingsLoading && !isLoading && isSuccess && !currentTeam) {
            if (teams.length > 0) {
                const favoriteTeam = profileSettings?.favoriteTeams?.[Config.SiteConfig.Site];
                const defaultTeam =
                    favoriteTeam && teams.find((team) => team.id === favoriteTeam) ? favoriteTeam : teams[0].id;
                setActiveTeam(defaultTeam);
            } else {
                setActiveTeam(null);
            }
        }
    }, [isLoading, isSuccess, currentTeam, isProfileSettingsLoading, ownedTeams, joinedTeams, pendingJoinTeams]);

    const hideMenu = () => setShowDropdown(false);

    const toggleMenu = () => {
        if (!showDropdown && onOpen) {
            onOpen();
        }

        setShowDropdown(!showDropdown);
        setShowJoin(false);
    };

    const manageTeams = () => {
        navigate('/profile/subscriptions/manage');
    };

    const selectTeam = (team: any) => {
        setActiveTeam(team.id);

        setShowDropdown(false);
        setShowUnpaidMessage(!team.paid && !team.pending);

        if (onSelect) {
            onSelect(team);
        }
    };

    const teamJoined = (result: any) => {
        setShowDropdown(false);
        setShowJoin(false);
        setJoinResult(result);
    };

    const dismissTeamJoined = () => setJoinResult(null);

    const doShowJoin = () => setShowJoin(true);

    const handleClickOutside = () => {
        if (showDropdown) {
            hideMenu();
        }
    };

    const hideUnpaidMessage = () => setShowUnpaidMessage(false);

    const doShowUnpaidMore = () => {
        setShowUnpaidMessage(false);
        setShowUnpaidMore(true);
    };

    const hideUnpaidMore = () => setShowUnpaidMore(false);

    const confirmLeaveTeam = () => {
        leaveTeam({ teamId: leavingTeam.id });
        setLeavingTeam(null);
    };

    const favoriteTeam = (team: Team) => {
        updateProfileSettings({
            uid,
            updates: {
                favoriteTeams: {
                    [Config.SiteConfig.Site]: team.id,
                },
            },
        });
    };

    let menuButton;
    if (isUninitialized || isLoading) {
        menuButton = (
            <div className="menu-button loading">
                <LoadingSpinner />
            </div>
        );
    } else if (!currentTeam) {
        menuButton = (
            <Button className="menu-button" onClick={toggleMenu} fullWidth>
                <AddIcon className="button-icon" />
                {APP ? 'Join' : 'Join/Create'} Team
            </Button>
        );
    } else {
        const name =
            currentTeam.name === currentProgram?.name || !currentProgram
                ? currentTeam.name
                : `${currentProgram?.name} - ${currentTeam.name}`;
        menuButton = (
            <div className="menu-button" onClick={toggleMenu}>
                <div className="team-item">
                    <span className="team-name">{name}</span>
                </div>
            </div>
        );
    }

    const allTeams = [...ownedTeams, ...joinedTeams, ...pendingJoinTeams];

    const teamsByProgram: Record<string, any[]> = allTeams.reduce((acc, team) => {
        const programId = team.programId || 'uncategorized';
        if (!acc[programId]) {
            acc[programId] = [];
        }
        acc[programId].push(team);
        return acc;
    }, {});

    const teamsLength = allTeams.length;

    const secondaryPrograms = programs
        .filter((program) => program.id !== currentProgram?.id)
        .sort((a, b) => a.name.localeCompare(b.name));

    const teamsListDisplay = secondaryPrograms.map((program) => {
        return (
            <TeamsMenuProgram
                key={program.id}
                program={program}
                teams={teamsByProgram[program.id]}
                selectTeam={selectTeam}
                leaveTeam={setLeavingTeam}
                favoriteTeam={favoriteTeam}
            />
        );
    });

    return (
        <ClickAwayListener onClickAway={handleClickOutside}>
            <div className={`teams-menu ${showDropdown ? 'open' : ''}`} data-testid="teams-menu">
                {showDropdown && currentProgram ? null : menuButton}
                {/* First program is rendered but hidden to ensure a smooth animation when a program logo is present */}
                {currentProgram ? (
                    <span className={showDropdown ? '' : 'd-none'}>
                        <TeamsMenuProgram
                            program={currentProgram}
                            teams={teamsByProgram[currentProgram.id]}
                            selectTeam={selectTeam}
                            leaveTeam={setLeavingTeam}
                            favoriteTeam={favoriteTeam}
                        />
                    </span>
                ) : null}
                {showDropdown ? (
                    <>
                        <div className="team-list">{teamsLength > 0 ? teamsListDisplay : undefined}</div>
                        <div className="team-menu-buttons-container">
                            <div className="team-menu-buttons">
                                {!APP ? (
                                    <Button onClick={manageTeams} className="confirm-button">
                                        <Plus className="button-icon" color="white" />
                                        Create Team
                                    </Button>
                                ) : null}

                                <Button onClick={doShowJoin}>
                                    <UserPlus className="button-icon" />
                                    Join Team
                                </Button>
                            </div>
                            {showJoin ? <JoinTeam className="p-2" onJoined={teamJoined} /> : null}
                        </div>
                    </>
                ) : null}

                <JoinTeamSuccess onClose={dismissTeamJoined} joinResult={joinResult} />
                <StyledSnackbar
                    onClose={hideUnpaidMessage}
                    message="This team is currently inactive"
                    action={[
                        <Button key="0" color="secondary" onClick={doShowUnpaidMore}>
                            Learn More
                        </Button>,
                    ]}
                    open={showUnpaidMessage}
                />
                <SimpleModal
                    open={showUnpaidMore}
                    text="The subscription for this team is currently inactive. If you are the owner of this team, you can update your subscription on the MANAGE TEAMS screen. Otherwise, contact your team admin."
                    onClose={hideUnpaidMore}
                />
                <SimpleModal
                    open={Boolean(leavingTeam)}
                    buttons={[
                        {
                            text: 'Cancel',
                        },
                        {
                            text: 'Leave',
                            variant: 'contained',
                            action: confirmLeaveTeam,
                            style: {
                                backgroundColor: '#dc3545',
                                color: 'white',
                            },
                        },
                    ]}
                    text={`Are you sure you want to leave ${leavingTeam?.name}?`}
                    onClose={() => setLeavingTeam(null)}
                />
            </div>
        </ClickAwayListener>
    );
};

export default TeamsMenu;
